import React from 'react';
import { Document, Page, Text, View, PDFDownloadLink, StyleSheet } from '@react-pdf/renderer';

const INK = '#1A1A1A';
const WIDTH = 227;

const styles = StyleSheet.create({
  page: {
    width: WIDTH,
    paddingTop: 14,
    paddingBottom: 16,
    paddingHorizontal: 12,
    fontFamily: 'Courier',
    fontSize: 8,
    color: INK,
    backgroundColor: '#FFFFFF',
  },
  center: { textAlign: 'center' },
  mark: {
    fontSize: 7,
    textAlign: 'center',
    marginBottom: 2,
  },
  company: {
    fontFamily: 'Courier-Bold',
    fontSize: 11,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 2,
  },
  line: {
    fontSize: 7.5,
    textAlign: 'center',
    marginBottom: 1,
  },
  rule: {
    borderBottomWidth: 0.6,
    borderBottomStyle: 'dashed',
    borderBottomColor: INK,
    marginVertical: 6,
  },
  title: {
    fontFamily: 'Courier-Bold',
    fontSize: 11,
    textAlign: 'center',
    letterSpacing: 0.6,
    marginVertical: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 1,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  name: { flex: 1, fontSize: 8, paddingRight: 6 },
  note: { fontSize: 7, marginTop: 1 },
  amount: { width: 62, fontSize: 8, textAlign: 'right' },
  label: { fontSize: 8 },
  value: { fontSize: 8, textAlign: 'right', maxWidth: 120 },
  total: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontFamily: 'Courier-Bold',
    fontSize: 11,
    marginTop: 3,
    marginBottom: 2,
  },
  end: {
    fontFamily: 'Courier-Bold',
    fontSize: 8,
    textAlign: 'center',
    marginTop: 2,
  },
});

const TENDERS: Record<string, string> = {
  mpesa: 'M-PESA',
  cash: 'CASH',
  card: 'CARD',
  split: 'SPLIT',
  pesalink: 'PESALINK',
  dpo: 'DPO',
  kopokopo: 'KOPOKOPO',
  bank_transfer: 'BANK',
  room_charge: 'ROOM',
  cheque: 'CHEQUE',
};

interface ReceiptItem {
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  status?: string;
  preparation_notes?: string;
}

interface ETimsReceiptProps {
  order: {
    order_number: string;
    order_type: string;
    status: string;
    payment_status: string;
    payment_method?: string;
    fiscal_reference?: string;
    table?: { display_name?: string; table_number?: string };
    guest_name?: string;
    guest_phone?: string;
    subtotal: number;
    tax_amount: number;
    discount_amount?: number;
    service_charge?: number;
    total_amount: number;
    paid_amount?: number;
    created_at: string;
    notes?: string;
    metadata?: { prices_include_vat?: boolean };
    items: ReceiptItem[];
  };
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyPin?: string;
  etimsSerial?: string;
  currency?: string;
}

function tenderLabel(method?: string): string {
  if (!method) return '';
  const key = method.toLowerCase();
  return TENDERS[key] || method.replace(/_/g, ' ').toUpperCase();
}

function money(amount: number): string {
  return Number(amount || 0).toLocaleString('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const ETimsReceiptDocument: React.FC<ETimsReceiptProps> = ({
  order,
  companyName = 'Resort',
  companyAddress,
  companyPhone,
  companyEmail,
  companyPin,
  etimsSerial,
}) => {
  const activeItems = order.items?.filter(item => item.status !== 'cancelled') || [];
  const subtotal = activeItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const taxAmount = order.tax_amount || 0;
  const discountAmount = order.discount_amount || 0;
  const serviceCharge = order.service_charge || 0;
  const inclusiveTotal = subtotal - discountAmount + serviceCharge;
  const exclusiveTotal = subtotal + taxAmount - discountAmount + serviceCharge;
  const storedTotal = order.total_amount;
  const flag = order.metadata?.prices_include_vat;
  const vatIncluded =
    flag === true ||
    (flag == null &&
      storedTotal != null &&
      Math.abs(storedTotal - inclusiveTotal) <= Math.abs(storedTotal - exclusiveTotal));
  const totalAmount = storedTotal ?? (vatIncluded ? inclusiveTotal : exclusiveTotal);
  const paidAmount = order.paid_amount || 0;
  const taxableGross = Math.max(0, subtotal - discountAmount);
  const taxBase = vatIncluded ? taxableGross - taxAmount : taxableGross;
  const rate = taxAmount > 0.009 && taxBase > 0.009 ? (taxAmount / taxBase) * 100 : 0;
  const rateLabel = rate > 0 ? (Math.abs(rate - 16) < 0.25 ? '16.00' : rate.toFixed(2)) : '';
  const taxCode = rateLabel ? 'A' : '';
  const articles = activeItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const tableName = order.table?.display_name || order.table?.table_number || '';
  const method = tenderLabel(order.payment_method);
  const when = new Date(order.created_at);
  const dateText = Number.isNaN(when.getTime())
    ? order.created_at
    : when.toLocaleDateString('en-GB');
  const timeText = Number.isNaN(when.getTime())
    ? ''
    : when.toLocaleTimeString('en-GB', { hour12: false });
  const noteLines = activeItems.filter(item => item.preparation_notes).length;
  const pageHeight = 360 + activeItems.length * 30 + noteLines * 12;

  return (
    <Document title={`Fiscal receipt ${order.order_number}`}>
      <Page size={[WIDTH, pageHeight]} style={styles.page}>
        <Text style={styles.mark}>*** START OF LEGAL RECEIPT ***</Text>
        <Text style={styles.company}>{companyName.toUpperCase()}</Text>
        {companyAddress ? <Text style={styles.line}>{companyAddress}</Text> : null}
        {companyPhone ? <Text style={styles.line}>TEL: {companyPhone}</Text> : null}
        {companyEmail ? <Text style={styles.line}>{companyEmail}</Text> : null}
        {companyPin ? <Text style={styles.line}>PIN: {companyPin}</Text> : null}

        <View style={styles.rule} />
        <Text style={styles.title}>FISCAL RECEIPT</Text>
        <View style={styles.rule} />

        <View style={styles.row}>
          <Text style={styles.label}>{dateText}</Text>
          <Text style={styles.value}>{timeText}</Text>
        </View>
        <Text style={[styles.line, { textAlign: 'left', marginTop: 2 }]}>{order.order_number}</Text>
        {tableName ? (
          <View style={styles.row}>
            <Text style={styles.label}>TABLE</Text>
            <Text style={styles.value}>{tableName}</Text>
          </View>
        ) : null}
        {order.guest_name ? (
          <View style={styles.row}>
            <Text style={styles.label}>GUEST</Text>
            <Text style={styles.value}>{order.guest_name}</Text>
          </View>
        ) : null}
        {order.order_type ? (
          <View style={styles.row}>
            <Text style={styles.label}>SERVICE</Text>
            <Text style={styles.value}>{order.order_type.replace(/_/g, ' ').toUpperCase()}</Text>
          </View>
        ) : null}

        <View style={styles.rule} />

        {activeItems.map((item, index) => (
          <View key={`${item.product_name}-${index}`} style={styles.item} wrap={false}>
            <View style={styles.name}>
              <Text>
                {item.quantity} {item.product_name}
              </Text>
              {item.preparation_notes ? (
                <Text style={styles.note}>{item.preparation_notes}</Text>
              ) : null}
            </View>
            <Text style={styles.amount}>
              {money(item.total_price)}
              {taxCode}
            </Text>
          </View>
        ))}

        <View style={styles.rule} />

        <View style={styles.row}>
          <Text style={styles.label}>SUBTOTAL</Text>
          <Text style={styles.value}>
            {money(subtotal)}
            {taxCode}
          </Text>
        </View>
        {discountAmount > 0 ? (
          <View style={styles.row}>
            <Text style={styles.label}>DISCOUNT</Text>
            <Text style={styles.value}>{money(-discountAmount)}</Text>
          </View>
        ) : null}
        {serviceCharge > 0 ? (
          <View style={styles.row}>
            <Text style={styles.label}>SERVICE</Text>
            <Text style={styles.value}>{money(serviceCharge)}</Text>
          </View>
        ) : null}
        <View style={styles.total}>
          <Text>TOTAL</Text>
          <Text>{money(totalAmount)}</Text>
        </View>

        {rateLabel ? (
          <View style={{ marginTop: 4 }}>
            <View style={styles.row}>
              <Text style={styles.label}>TOTAL A-{rateLabel}%</Text>
              <Text style={styles.value}>
                {money(vatIncluded ? taxableGross : taxableGross + taxAmount)}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>TOTAL TAX A</Text>
              <Text style={styles.value}>{money(taxAmount)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>TOTAL TAXES</Text>
              <Text style={styles.value}>{money(taxAmount)}</Text>
            </View>
          </View>
        ) : null}

        <View style={styles.rule} />

        {method ? (
          <View style={styles.row}>
            <Text style={styles.label}>{method}</Text>
            <Text style={styles.value}>{money(paidAmount || totalAmount)}</Text>
          </View>
        ) : null}
        <Text style={styles.line}>
          {String(articles).padStart(2, '0')} ARTICLE{articles === 1 ? '' : 'S'}
        </Text>
        {order.fiscal_reference ? (
          <Text style={[styles.line, { marginTop: 3 }]}>REF {order.fiscal_reference}</Text>
        ) : null}

        {etimsSerial ? (
          <View>
            <View style={styles.rule} />
            <Text style={styles.line}>CONTROL UNIT INFO</Text>
            <Text style={styles.line}>CU SERIAL NO {etimsSerial}</Text>
          </View>
        ) : null}

        <View style={styles.rule} />
        <Text style={styles.end}>FISCAL RECEIPT</Text>
        <Text style={[styles.mark, { marginTop: 4 }]}>*** END OF LEGAL RECEIPT ***</Text>
      </Page>
    </Document>
  );
};

interface ETimsDownloadButtonProps {
  order: ETimsReceiptProps['order'];
  companyName?: string;
  filename?: string;
}

export const ETimsReceiptDownload: React.FC<ETimsDownloadButtonProps> = ({
  order,
  companyName,
  filename = 'fiscal-receipt.pdf',
}) => (
  <PDFDownloadLink
    document={<ETimsReceiptDocument order={order} companyName={companyName} />}
    fileName={filename}
  >
    Fiscal Receipt
  </PDFDownloadLink>
);

export default ETimsReceiptDocument;
